import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import { IoArrowBackSharp } from "react-icons/io5";

const EditSolution = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    credits: "",
    type: "TUNING", // Default value
    order_no: "",
    image_url: "",
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [images, setImages] = useState([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSolution = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/solutions/${id}`);
        const solution = response.data;
        setFormData({
          name: solution.name,
          code: solution.code,
          credits: solution.credits.toString(),
          type: solution.type,
          order_no: solution.order_no ? solution.order_no.toString() : "",
          image_url: solution.image_url || "",
        });
        setSelectedImageUrl(solution.image_url || "");
      } finally {
        setLoading(false);
      }
    };

    const fetchImages = async () => {
      try {
        const response = await axiosInstance.get("/solutions/images");
        setImages(response.data);
      } catch (err) {
        setError("Failed to load images.");
      }
    };

    fetchSolution();
    fetchImages();
  }, [id]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle image selection
  const handleImageSelect = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
    setShowImageModal(false); // Close the modal after selecting an image
    setFormData({
      ...formData,
      image_url: imageUrl, // Update the formData with the selected image URL
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axiosInstance.put(`/solutions/${id}`, {
        name: formData.name,
        code: formData.code,
        credits: parseInt(formData.credits, 10),
        type: formData.type,
        order_no: parseInt(formData.order_no, 10),
        image_url: selectedImageUrl,
      });
      setLoading(false);
      navigate("/admin/solutions");
    } catch (err) {
      setError("Failed to update solution. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
      <h3 className="mb-1 flex items-center gap-3 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
        {/* Go Back Button */}
        <button
          onClick={() => window.history.back()} // This will navigate back to the previous page
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition hover:border-red-400/60 hover:text-red-500 dark:border-[#3a3a3d] dark:text-slate-400"
        >
          <IoArrowBackSharp size={24} />
        </button>
                <span className="h-6 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-red-500 to-orange-400" />
        Edit Solution
      </h3>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="p-6 rounded-[22px] border border-gray-200 bg-white shadow-lg shadow-gray-900/5 dark:border-[#2c2c2e] dark:bg-[#1c1c1e] dark:shadow-black/40"
      >
        <div className="mb-4">
          <label
            htmlFor="name"
            className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
          >
            Solution Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
            required
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="code"
            className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
          >
            Code
          </label>
          <input
            type="text"
            id="code"
            name="code"
            value={formData.code}
            onChange={handleChange}
            className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
            required
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="credits"
            className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
          >
            Credits
          </label>
          <input
            type="number"
            id="credits"
            name="credits"
            value={formData.credits}
            onChange={handleChange}
            className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
            required
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="type"
            className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
          >
            Type
          </label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
          >
            <option value="TUNING">TUNING</option>
            <option value="OPTION">OPTION</option>
          </select>
        </div>

        <div className="mb-4">
          <label
            htmlFor="order_no"
            className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
          >
            Order Number
          </label>
          <input
            type="number"
            id="order_no"
            name="order_no"
            value={formData.order_no}
            onChange={handleChange}
            className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
          />
        </div>

        {/* Image Selection */}
        <div className="mb-4">
          <label
            htmlFor="image"
            className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
          >
            Select Image
          </label>
          <button
            type="button"
            onClick={() => setShowImageModal(true)} // Open image modal
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-600 to-orange-500 px-6 text-sm font-bold text-white shadow-lg shadow-red-500/25 transition hover:from-red-500 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {selectedImageUrl ? "Change Image" : "Select Image"}
          </button>

          {selectedImageUrl && (
            <div className="mt-4">
              <p>Selected Image:</p>
              <img
                src={selectedImageUrl}
                alt="Selected"
                className="w-32 h-32 object-cover border"
              />
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && <p className="mb-4 text-sm font-medium text-rose-600 dark:text-rose-400">{error}</p>}

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className={`px-4 py-2 text-white rounded-md ${
              loading ? "bg-gray-400" : "bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400"
            } focus:outline-none`}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Solution"}
          </button>
        </div>
      </form>

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-2xl p-6 rounded-[22px] border border-gray-200 bg-white shadow-lg shadow-gray-900/5 dark:border-[#2c2c2e] dark:bg-[#1c1c1e] dark:shadow-black/40">
            <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">Select an Image</h2>
            <div className="grid grid-cols-4 gap-4 h-auto max-h-[80vh] overflow-y-auto">
              {images.map((image) => (
                <div key={image.id} className="cursor-pointer">
                  <img
                    src={image.url}
                    alt={image.name}
                    className="w-full h-32 object-cover border"
                    onClick={() => handleImageSelect(image.url)}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button
                className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-rose-500 px-6 text-sm font-bold text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => setShowImageModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditSolution;
